/**
 * Price calculation utilities
 *
 * "Shop smart. Shop S-Mart."
 */

/**
 * Get the original line total before discount using the full tax-inclusive price.
 * Used for cart/checkout strikethrough pricing when a coupon is applied.
 *
 * `basePrice` is the pre-discount line total before tax. When a coupon is applied,
 * users expect the crossed-out amount to reflect the same tax-inclusive pricing
 * basis as the final line price they are comparing against.
 *
 * @param {Object} orderLine - The order line object (needs basePrice or preTaxPrice, quantity)
 * @param {Object} order - The order object (uses taxRate.rate when available)
 * @returns {number} The original line total before discount, including tax
 */
export function getOrderLineOriginalPrice(orderLine, order) {
  const basePrice = Number(orderLine?.basePrice || 0);
  const preTaxPrice = Number(orderLine?.preTaxPrice || 0);
  const quantity = orderLine?.quantity || 1;
  const taxRate = Number(order?.taxRate?.rate || 0);
  const originalPreTaxTotal = preTaxPrice > 0 ? (preTaxPrice * quantity) : basePrice;

  return Number((originalPreTaxTotal * (1 + taxRate)).toFixed(2));
}

/**
 * Get the discounted line total using the same tax-inclusive basis as the
 * original line price helper above.
 *
 * This avoids mismatches where `orderLine.price` and the reconstructed
 * original amount are rounded differently for integer currencies.
 *
 * @param {Object} orderLine - The order line object
 * @param {Object} order - The parent order object
 * @returns {number} The final discounted line total including tax
 */
export function getOrderLineFinalPrice(orderLine, order) {
  const basePrice = Number(orderLine?.basePrice || 0);
  const preTaxPrice = Number(orderLine?.preTaxPrice || 0);
  const discount = Number(orderLine?.discount || 0);
  const fallbackPrice = Number(orderLine?.price || 0);
  const quantity = orderLine?.quantity || 1;
  const taxRate = Number(order?.taxRate?.rate || 0);
  const originalPreTaxTotal = preTaxPrice > 0 ? (preTaxPrice * quantity) : basePrice;

  if (originalPreTaxTotal <= 0) {
    return fallbackPrice;
  }

  const discountedPreTaxTotal = Math.max(originalPreTaxTotal - discount, 0);
  return Number((discountedPreTaxTotal * (1 + taxRate)).toFixed(2));
}

/**
 * Get the order-level coupon discount using the full tax-inclusive amount.
 *
 * The backend exposes `totalDiscount` as a pre-tax number. For checkout/cart
 * summaries we want the visible discount row to match the reduction seen in
 * the tax-inclusive total.
 *
 * @param {Object} order - The order object (needs totalDiscount and taxRate.rate)
 * @returns {number} Discount including tax, rounded to currency precision
 */
export function getOrderDiscountWithTax(order) {
  const preTaxDiscount = Number(order?.totalDiscount || 0);
  const taxRate = Number(order?.taxRate?.rate || 0);

  if (preTaxDiscount <= 0) {
    return 0;
  }

  return Number((preTaxDiscount * (1 + taxRate)).toFixed(2));
}
